import React, { useEffect, useState } from "react";
import axios from "axios";
import { config } from "../../../config";
import UpdateUser from "./UpdateUser";
import UserMembership from "./UserMembership";

const ViewUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editId, setEditId] = useState(null);
  const apiUrl = config.apiUrl;
  const [success, setSuccess] = useState('');
  const [memberships, setMemberships] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    membership_type: '',
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const usersPerPage = 10;

  // Fetch users from API with pagination
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${apiUrl}/users`, {
          params: {
            page: currentPage,
            per_page: usersPerPage,
            sort: 'newest' // Assuming your backend supports this parameter
          }
        });
        
        // If your API returns pagination data in the response
        if (response.data.data) {
          setUsers(response.data.data);
          setTotalPages(response.data.last_page);
          setTotalUsers(response.data.total);
        } else {
          // Fallback if API doesn't return pagination data
          setUsers(response.data);
          setTotalPages(Math.ceil(response.data.length / usersPerPage));
          setTotalUsers(response.data.length);
        }
        
        setLoading(false);
      } catch (err) {
        console.error("Error fetching users:", err);
        setError("Failed to load users. Please try again later.");
        setLoading(false);
      }
    };

    fetchUsers();
  }, [apiUrl, currentPage]);

  // Handle membership renewal
  const handleRenew = async (userId, membershipId) => {
    try {
      const response = await axios.post(
        `${apiUrl}/user/${userId}/membership/${membershipId}/renew`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      setUsers(prevUsers => prevUsers.map(user => {
        if (user.id === userId) {
          return {
            ...user,
            memberships: user.memberships.map(m => {
              if (m.id === membershipId) {
                return {
                  ...m,
                  pivot: {
                    ...m.pivot,
                    end_date: response.data.end_date
                  }
                };
              }
              return m;
            })
          };
        }
        return user;
      }));

      setSuccess("Membership renewed successfully!");
    } catch (err) {
      console.error("Error renewing membership:", err);
      setError(err.response?.data?.error || "Failed to renew membership");
    }
  };

  const handleLifetime = async (userId, membershipId) => {
    try {
      const response = await axios.post(
        `${apiUrl}/user/${userId}/membership/${membershipId}/lifetime`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      setUsers(prevUsers => prevUsers.map(user => {
        if (user.id === userId) {
          return {
            ...user,
            memberships: user.memberships.map(m => {
              if (m.id === membershipId) {
                return {
                  ...m,
                  pivot: {
                    ...m.pivot,
                    end_date: null
                  }
                };
              }
              return m;
            })
          };
        }
        return user;
      }));

      setSuccess("Membership set to lifetime successfully!");
    } catch (err) {
      console.error("Error setting lifetime membership:", err);
      setError(err.response?.data?.message || "Failed to set lifetime membership");
    }
  };

  // Fetch memberships from API
  useEffect(() => {
    const fetchMemberships = async () => {
      try {
        const response = await axios.get(`${apiUrl}/memberships`);
        setMemberships(response.data);
      } catch (err) {
        console.error("Error fetching memberships:", err);
        setError("Failed to load memberships. Please try again later.");
      }
    };

    fetchMemberships();
  }, [apiUrl]);

  // Handle Approve/Unapprove
  const toggleApproval = async (id, currentApproval) => {
    try {
      await axios.post(`${apiUrl}/marketers/${id}/toggleApproval`);
      setUsers((prev) =>
        prev.map((marketer) =>
          marketer.id === id ? { ...marketer, approved: !currentApproval } : marketer
        )
      );
      setSuccess("Approval status updated successfully!");
    } catch (err) {
      console.error("Error updating approval status:", err);
      setError("Failed to update approval status. Please try again.");
    }
  };

  // Handle Role Update
  const updateRole = async (id, role) => {
    const roles = ["client", "admin", "affiliates"];
    const currentIndex = roles.indexOf(role);
    const nextRole = roles[(currentIndex + 1) % roles.length];

    try {
      await axios.post(`${apiUrl}/marketers/${id}/updateRole`, { role: nextRole });
      setUsers((prev) =>
        prev.map((marketer) => (marketer.id === id ? { ...marketer, role: nextRole } : marketer))
      );
      setSuccess("Role updated successfully!");
    } catch (err) {
      console.error("Error updating role:", err);
      setError("Failed to update role. Please try again.");
    }
  };

  // Handle Edit
  const handleEdit = (user) => {
    setEditId(user.id);
    setFormData({
      name: user.name,
      email: user.email,
      phone: user.phone,
      membership_type: user.membership_type,
    });
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(`${apiUrl}/users/update/${editId}`, formData);
      setUsers((prev) =>
        prev.map((user) => (user.id === editId ? { ...user, ...formData } : user))
      );
      setSuccess("User updated successfully!");
      setEditId(null);
    } catch (err) {
      console.error("Error updating user:", err);
      setError("Failed to update user. Please try again.");
    }
  }

  // Handle Delete
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await axios.delete(`${apiUrl}/marketers/${id}`);
        setUsers((prev) => prev.filter((user) => user.id !== id));
        setSuccess("User deleted successfully!");
        
        // If we're on the last page with only one item, go back a page
        if (users.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        }
      } catch (err) {
        console.error("Error deleting user:", err);
        setError("Failed to delete user. Please try again.");
      }
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'Lifetime';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Pagination controls
  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = 1;
    let endPage = totalPages;

    if (totalPages > maxVisiblePages) {
      const half = Math.floor(maxVisiblePages / 2);
      startPage = Math.max(currentPage - half, 1);
      endPage = Math.min(currentPage + half, totalPages);

      if (currentPage <= half + 1) {
        endPage = maxVisiblePages;
      } else if (currentPage >= totalPages - half) {
        startPage = totalPages - maxVisiblePages + 1;
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => goToPage(i)}
          className={`px-3 py-1 rounded mx-1 ${currentPage === i ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
        >
          {i}
        </button>
      );
    }

    return (
      <div className="flex justify-center mt-4">
        <button
          onClick={() => goToPage(1)}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded mx-1 bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
        >
          «
        </button>
        <button
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded mx-1 bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
        >
          ‹
        </button>
        
        {startPage > 1 && (
          <>
            <button
              onClick={() => goToPage(1)}
              className="px-3 py-1 rounded mx-1 bg-gray-200 hover:bg-gray-300"
            >
              1
            </button>
            {startPage > 2 && <span className="px-2 py-1">...</span>}
          </>
        )}

        {pages}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="px-2 py-1">...</span>}
            <button
              onClick={() => goToPage(totalPages)}
              className="px-3 py-1 rounded mx-1 bg-gray-200 hover:bg-gray-300"
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 rounded mx-1 bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
        >
          ›
        </button>
        <button
          onClick={() => goToPage(totalPages)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 rounded mx-1 bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
        >
          »
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4" role="alert">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-800">Users Management</h2>
          <p className="text-sm text-gray-600">
            Showing {(currentPage - 1) * usersPerPage + 1} to {Math.min(currentPage * usersPerPage, totalUsers)} of {totalUsers} users
          </p>
        </div>

        {success && (
          <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mx-6 mt-4 rounded">
            <p>{success}</p>
            <button 
              onClick={() => setSuccess('')} 
              className="float-right text-green-700 hover:text-green-900"
            >
              ×
            </button>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Referral Code</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Membership</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.referral_code || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${user.balance || '0.00'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.memberships.length > 0 ? (
                      user.memberships.map(m => (
                        <div key={`${user.id}-${m.id}`} className="mb-2 last:mb-0">
                          <div className="flex items-center justify-between">
                            <span>{m.membership_type}</span>
                            <button 
                              onClick={() => handleRenew(user.id, m.id)}
                              className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded hover:bg-green-200"
                            >
                              Renew
                            </button>
                            <button 
                              onClick={() => handleLifetime(user.id, m.id)}
                              className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded hover:bg-blue-200"
                            >
                              Lifetime
                            </button>
                          </div>
                          <div className="text-xs text-gray-500">
                            Ends: {formatDate(m.pivot.end_date)}
                          </div>
                        </div>
                      ))
                    ) : 'No Membership'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.memberships.length > 0
                      ? user.memberships.map(m => formatDate(m.pivot.end_date)).join(', ')
                      : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => updateRole(user.id, user.role)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        user.role === "client"
                          ? "bg-blue-100 text-blue-800 hover:bg-blue-200"
                          : user.role === "admin"
                          ? "bg-purple-100 text-purple-800 hover:bg-purple-200"
                          : "bg-green-100 text-green-800 hover:bg-green-200"
                      }`}
                    >
                      {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      user.approved ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {user.approved ? "Approved" : "Pending"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => toggleApproval(user.id, user.approved)}
                        className={`text-xs px-3 py-1 rounded ${
                          user.approved 
                            ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' 
                            : 'bg-green-100 text-green-800 hover:bg-green-200'
                        }`}
                      >
                        {user.approved ? "Unapprove" : "Approve"}
                      </button>
                      <button
                        onClick={() => handleEdit(user)}
                        className="text-xs bg-blue-100 text-blue-800 hover:bg-blue-200 px-3 py-1 rounded"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(user.id)}
                        className="text-xs bg-red-100 text-red-800 hover:bg-red-200 px-3 py-1 rounded"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {users.length === 0 && !loading && (
          <div className="text-center py-8">
            <p className="text-gray-500">No users found</p>
          </div>
        )}

        {totalPages > 1 && renderPagination()}
      </div>

      {editId && (
        <div className="mt-8">
          <UpdateUser 
            editId={editId}
            formData={formData}
            setFormData={setFormData}
            handleSubmit={handleSubmit}
            memberships={memberships}
            setEditId={setEditId}
            setSuccess={setSuccess}
            onClose={() => setEditId(null)}
          />
        </div>
      )}
    </div>
  );
};

export default ViewUsers;